import React from "react";
import { Link } from "react-router-dom";
import { StatusPill, DemoChip } from "@/components/Chips";
import { ActionMenu } from "@/components/ActionMenu";

// Wave item 2 (ops-ux-documents-2026-09-23) — Timeline view for the
// Tours workspace. Chronological, month-grouped, each card is an
// accessible link (Enter + Space activate via native <a>), so keyboard
// users get first-class navigation. The inner ActionMenu stops
// propagation on all interactive controls so opening the row menu
// never triggers the row navigation.
//
// Contract with ToursList:
//   * `rows` — normal tours API result set
//   * `canWrite` — controls edit / archive availability inside the row menu
//   * `onEdit(row)` / `onArchive(row)` — callbacks; ActionMenu wraps
//     them so the underlying <Link> is not activated on click.
//   * every card exposes data-testid `tour-timeline-card-<tour_number>`.

const _monthLabel = (iso) => {
  if (!iso) return "Undated";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Undated";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const _dateRange = (start, end) => {
  if (!start && !end) return "TBA";
  const fmt = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return `${fmt(start)} → ${fmt(end)}`;
};

const _groupByMonth = (rows) => {
  const groups = new Map();
  for (const r of rows) {
    const key = _monthLabel(r.start_date);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  // Preserve natural row order per group (rows arrive sorted by
  // start_date desc from the API); groups themselves are kept in
  // first-seen order.
  return Array.from(groups.entries());
};

export const TourTimeline = ({ rows, canWrite, onEdit, onArchive, backState }) => {
  if (rows === null) {
    return (
      <div className="text-center text-steel text-[13px] py-10" data-testid="tour-timeline-loading">
        Loading tours…
      </div>
    );
  }
  if (rows && rows.length === 0) {
    return (
      <div className="text-center text-steel text-[13px] py-10" data-testid="tour-timeline-empty">
        No tours match the current filters.
      </div>
    );
  }
  const groups = _groupByMonth(rows);
  return (
    <div className="space-y-6 p-3" data-testid="tour-timeline">
      {groups.map(([month, groupRows]) => (
        <section key={month} data-testid={`tour-timeline-month-${month.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="text-[11px] uppercase tracking-widest text-steel mb-2 px-1">
            {month}
          </div>
          <ol className="space-y-2">
            {groupRows.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/tours/${r.id}`}
                  state={backState}
                  data-testid={`tour-timeline-card-${r.tour_number}`}
                  className="group block border border-coach-silver rounded-md bg-white hover:border-coach-blue focus:border-coach-blue focus:outline-none transition-colors"
                >
                  <div className="flex items-start gap-3 p-3">
                    <div className="w-16 shrink-0 pt-0.5">
                      <div className="font-mono text-[11.5px] text-steel">
                        {r.tour_number}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13.5px] font-medium text-carbon group-hover:text-coach-blue truncate">
                          {r.display_name}
                        </span>
                        {r.is_demo && <DemoChip />}
                      </div>
                      <div className="text-[12px] text-steel truncate">
                        {r.artist_display ? `${r.artist_display} · ` : ""}
                        {_dateRange(r.start_date, r.end_date)}
                      </div>
                    </div>
                    <div className="shrink-0 self-center">
                      <StatusPill status={r.status} />
                    </div>
                    <div
                      className="shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                      data-testid={`tour-timeline-actions-${r.tour_number}`}
                    >
                      <ActionMenu
                        testid={`tour-timeline-actions-menu-${r.tour_number}`}
                        onEdit={canWrite ? () => onEdit?.(r) : undefined}
                        onArchive={canWrite && !r.is_archived ? () => onArchive?.(r) : undefined}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
};

export default TourTimeline;
